import { normalizeRoleToKey } from "../../../../Enums/UserEnums";

export const PERSON_ROLE_DATA_ADMIN = "dataAdmin" as const;
export const PERSON_ROLE_MEMBER = "member" as const;
export const PERSON_ROLE_WSG_STAFF = "wsgStaff" as const;
export const PERSON_ROLE_WSG_SUPER = "wsgSuper" as const;

export const DATA_ADMIN_CANNOT_ASSIGN_DATA_ADMIN_MESSAGE =
  "Data admins can only assign the Member role";

export const WSG_STAFF_CANNOT_ASSIGN_STAFF_MESSAGE =
  "WSG Staff can only assign Member and Data Admin + Member roles";

export const WSG_STAFF_CANNOT_ASSIGN_SUPER_STAFF_MESSAGE =
  "Staff can only assign the Staff role";

const PROFESSIONAL_SCREEN_ROLE_KEYS = [
  PERSON_ROLE_MEMBER,
  PERSON_ROLE_DATA_ADMIN,
] as const;

const ALL_ROLE_KEYS = [
  PERSON_ROLE_MEMBER,
  PERSON_ROLE_DATA_ADMIN,
  PERSON_ROLE_WSG_STAFF,
  PERSON_ROLE_WSG_SUPER,
] as const;

export function isPersonRoleDataAdmin(role: unknown): boolean {
  return normalizeRoleToKey(String(role ?? "")) === PERSON_ROLE_DATA_ADMIN;
}

export function isDataAdminActor(actorRole: string | undefined): boolean {
  return isPersonRoleDataAdmin(actorRole);
}

function uniqueRoleKeys(keys: readonly string[]): string[] {
  return [...new Set(keys.filter(Boolean))];
}

/**
 * Roles this actor may assign system-wide.
 * Professional UI still only lists member + dataAdmin even for wsgSuper.
 */
export function getAssignablePersonRoleKeys(
  actorRole: string | undefined,
  existingRole?: string,
): string[] {
  const actorRoleKey = normalizeRoleToKey(actorRole);
  const existingRoleKey = normalizeRoleToKey(existingRole);

  if (actorRoleKey === PERSON_ROLE_WSG_SUPER) {
    return [...ALL_ROLE_KEYS];
  }

  if (actorRoleKey === PERSON_ROLE_WSG_STAFF) {
    return [...PROFESSIONAL_SCREEN_ROLE_KEYS];
  }

  if (actorRoleKey === PERSON_ROLE_DATA_ADMIN) {
    if (existingRoleKey === PERSON_ROLE_DATA_ADMIN) {
      return uniqueRoleKeys([PERSON_ROLE_MEMBER, PERSON_ROLE_DATA_ADMIN]);
    }
    return [PERSON_ROLE_MEMBER];
  }

  return [PERSON_ROLE_MEMBER];
}

/** Role keys shown on Add/Edit Professional (never staff roles). */
export function getProfessionalScreenRoleKeys(
  actorRole: string | undefined,
  existingRole?: string,
): string[] {
  const assignable = new Set(getAssignablePersonRoleKeys(actorRole, existingRole));
  return PROFESSIONAL_SCREEN_ROLE_KEYS.filter((key) => assignable.has(key));
}

/** Role keys shown on WSG Staff Profiles. */
export function getWsgStaffScreenRoleKeys(
  actorRole: string | undefined,
): string[] {
  const actorRoleKey = normalizeRoleToKey(actorRole);
  if (actorRoleKey === PERSON_ROLE_WSG_SUPER) {
    return [PERSON_ROLE_WSG_STAFF, PERSON_ROLE_WSG_SUPER];
  }
  if (actorRoleKey === PERSON_ROLE_WSG_STAFF) {
    return [PERSON_ROLE_WSG_STAFF];
  }
  return [];
}

export function canActorAssignPersonRole(
  actorRole: string | undefined,
  targetRole: string | undefined,
  existingRole?: string,
  options?: { staffProfilesScreen?: boolean },
): boolean {
  const targetRoleKey = normalizeRoleToKey(targetRole);
  if (!targetRoleKey) {
    return true;
  }
  if (options?.staffProfilesScreen) {
    return getWsgStaffScreenRoleKeys(actorRole).includes(targetRoleKey);
  }
  return getAssignablePersonRoleKeys(actorRole, existingRole).includes(
    targetRoleKey,
  );
}

export function getPersonRoleAssignmentErrorMessage(
  actorRole: string | undefined,
  options?: { staffProfilesScreen?: boolean },
): string {
  const actorRoleKey = normalizeRoleToKey(actorRole);
  if (options?.staffProfilesScreen) {
    return WSG_STAFF_CANNOT_ASSIGN_SUPER_STAFF_MESSAGE;
  }
  if (actorRoleKey === PERSON_ROLE_WSG_STAFF) {
    return WSG_STAFF_CANNOT_ASSIGN_STAFF_MESSAGE;
  }
  if (actorRoleKey === PERSON_ROLE_DATA_ADMIN) {
    return DATA_ADMIN_CANNOT_ASSIGN_DATA_ADMIN_MESSAGE;
  }
  return DATA_ADMIN_CANNOT_ASSIGN_DATA_ADMIN_MESSAGE;
}

export function getCreatedPersonId(result: unknown): string {
  if (!result || typeof result !== "object" || !("id" in result)) {
    return "";
  }
  const id = (result as { id?: unknown }).id;
  return id != null ? String(id).trim() : "";
}
